import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Row, Col, Card, Button, Spinner } from "react-bootstrap";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";

export default function GalleryUpload({ pId, images: initialImages = [] }) {
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [savingOrder, setSavingOrder] = useState(false);
    const fileInputRef = useRef(null);
    const draggedIdRef = useRef(null);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("authToken") ||
            localStorage.getItem("AuthUser")
            }`,
    });

    useEffect(() => {
        setImages(
            [...initialImages].sort(
                (a, b) =>
                    (Number(a.sequence_no) || 0) -
                    (Number(b.sequence_no) || 0) ||
                    a.id - b.id
            )
        );
    }, [initialImages]);

    const getImageUrl = (item) => {
        // Sabhi possible image keys ko check karein taake path miss na ho
        const imagePath = item?.image || item?.img || item?.path || item?.url || item?.file || "";

        if (!imagePath) return "";

        if (imagePath.startsWith("http") || imagePath.startsWith("blob:")) {
            return imagePath;
        }

        return `${process.env.REACT_APP_IMG_URL}/${imagePath.replace(/^\//, "")}`;
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setUploading(true);
        const toastId = toast.loading("Uploading image(s)...");

        try {
            const uploadedImages = [];

            for (const file of files) {
                const formData = new FormData();
                formData.append("id", pId);
                formData.append("image", file);
                formData.append("model", "products");

                const res = await axios.post(
                    `${process.env.REACT_APP_API_URL}/product-creator/upload-image`,
                    formData,
                    {
                        headers: getAuthHeaders(),
                    }
                );

                console.log("Upload Response Data:", res.data); // Debugging ke liye

                // Backend response structure ke mutabiq image object extract karein
                const newImgObj = res.data.data || res.data.image || res.data;
                if (newImgObj) {
                    uploadedImages.push(newImgObj);
                }
            }

            setImages((prev) => [...prev, ...uploadedImages]);
            toast.success("Image(s) uploaded successfully!", { id: toastId });
        } catch (err) {
            console.error("Upload error:", err);
            toast.error(
                err.response?.data?.message || "Failed to upload image(s).",
                { id: toastId }
            );
        } finally {
            setUploading(false);

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handleDrop = async (e, targetId) => {
        e.preventDefault();
        e.stopPropagation();

        const draggedId = draggedIdRef.current;
        draggedIdRef.current = null;

        if (!draggedId || Number(draggedId) === Number(targetId) || savingOrder) return;

        const fromIndex = images.findIndex(
            (item) => Number(item.id) === Number(draggedId)
        );
        const toIndex = images.findIndex(
            (item) => Number(item.id) === Number(targetId)
        );

        if (fromIndex < 0 || toIndex < 0) return;

        const previousImages = [...images];
        const reordered = [...images];
        const [movedImage] = reordered.splice(fromIndex, 1);
        reordered.splice(toIndex, 0, movedImage);

        const updatedImages = reordered.map((item, index) => ({
            ...item,
            sequence_no: index + 1,
        }));

        setImages(updatedImages);
        setSavingOrder(true);

        const toastId = toast.loading("Saving image order...");

        try {
            const payloadIds = updatedImages.map((item) => Number(item.id));

            await axios.post(
                `${process.env.REACT_APP_API_URL}/lister/update-image-sequence`,
                {
                    product_id: Number(pId),
                    image_ids: payloadIds,
                },
                {
                    headers: getAuthHeaders(),
                }
            );

            toast.success("Image order saved!", { id: toastId });
        } catch (err) {
            setImages(previousImages);
            console.error("Sequence update error:", err);
            toast.error(
                err.response?.data?.message || "Image order save nahi hua.",
                { id: toastId }
            );
        } finally {
            setSavingOrder(false);
        }
    };

    const deleteImage = async (id) => {
        if (!window.confirm("Are you sure you want to delete this image?")) {
            return;
        }

        const toastId = toast.loading("Deleting image...");

        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/lister/delete-image/${id}`,
                {
                    headers: getAuthHeaders(),
                }
            );

            setImages((prev) => prev.filter((item) => item.id !== id));
            toast.success("Image deleted successfully!", { id: toastId });
        } catch (err) {
            console.error("Delete error:", err);
            toast.error(
                err.response?.data?.message || "Failed to delete image.",
                { id: toastId }
            );
        }
    };

    return (
        <Row className="g-3">
            {images.map((item, index) => (
                <Col md={3} sm={6} xs={12} key={item.id || index}>
                    <Card
                        draggable={!uploading && !savingOrder}
                        onDragStart={(e) => {
                            draggedIdRef.current = item.id;
                            e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(e) => handleDrop(e, item.id)}
                        onDragEnd={() => {
                            draggedIdRef.current = null;
                        }}
                        className="shadow-sm border-0 h-100"
                        style={{
                            cursor: savingOrder ? "wait" : "grab",
                        }}
                    >
                        <div
                            style={{
                                position: "relative",
                                height: "180px",
                                overflow: "hidden",
                                borderRadius: "0.375rem 0.375rem 0 0",
                            }}
                        >
                            <Card.Img
                                variant="top"
                                src={getImageUrl(item)}
                                alt={`Gallery image ${index + 1}`}
                                draggable={false}
                                style={{
                                    height: "100%",
                                    width: "100%",
                                    objectFit: "cover",
                                }}
                            />

                            <span
                                className="badge bg-dark position-absolute"
                                style={{ top: 8, left: 8 }}
                            >
                                {index + 1}
                            </span>
                        </div>

                        <Card.Body className="text-center p-2">
                            <small className="d-block text-muted mb-2">
                                Drag to reorder
                            </small>

                            <Button
                                variant="danger"
                                size="sm"
                                className="d-inline-flex align-items-center gap-1"
                                disabled={savingOrder}
                                onClick={() => deleteImage(item.id)}
                            >
                                <FiTrash2 size={16} /> Delete
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            ))}

            <Col md={3} sm={6} xs={12}>
                <Card
                    className="shadow-sm d-flex flex-column justify-content-center align-items-center text-center p-3"
                    style={{
                        height: "235px",
                        cursor: uploading ? "not-allowed" : "pointer",
                        border: "2px dashed var(--bs-primary, #0d6efd)",
                        backgroundColor: "#f8f9fa",
                    }}
                    onClick={() => {
                        if (!uploading && !savingOrder) {
                            fileInputRef.current?.click();
                        }
                    }}
                >
                    {uploading ? (
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" size="sm" />
                            <div className="mt-2 text-muted fw-semibold">
                                Uploading...
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center mb-2"
                                style={{ width: 48, height: 48 }}
                            >
                                <FiPlus size={24} />
                            </div>
                            <div className="fw-bold text-primary">Add Images</div>
                            <small className="text-muted">Click to browse files</small>
                        </>
                    )}
                </Card>

                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileUpload}
                    disabled={uploading || savingOrder}
                />
            </Col>
        </Row>
    );
}